import React, {
  forwardRef, useEffect, useImperativeHandle, useState,
} from 'react';
import {
  Box, Button, CircularProgress, Collapse, Popper, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { BlockCollapseToggle, DOCK_SCROLLBAR_SX } from '../../Map/components/DockPanel/Tabs/formBlockUi';
import useLeadRecordSearch from './useLeadRecordSearch';
import {
  describeLeadRecordCounts,
  describeLeadRecordFilter,
  describeLeadRecordLists,
  getLeadRecordKindLabel,
  normalizeLeadRecordFilter,
} from '../leadRecordSearch';
import { LEAD_RECORD_SEARCH_MIN } from '../../services/leadsService';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import {
  LEADS_PANEL_BG,
  LEADS_SELECTED_ACCENT,
  LEADS_TEXT_MUTED,
  LEADS_TEXT_PRIMARY,
  LEADS_TEXT_SECONDARY,
  leadsSummaryClearButtonSx,
} from './leadsUi.styles';

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  minWidth: 0,
  px: 1,
  py: 0.75,
  borderRadius: '6px',
  textAlign: 'left',
  textTransform: 'none',
  justifyContent: 'flex-start',
  color: LEADS_TEXT_PRIMARY,
  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
};
const titleSx = { fontSize: '13px', color: LEADS_TEXT_PRIMARY, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const metaSx = { fontSize: '11px', color: LEADS_TEXT_MUTED, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const kindSx = { fontSize: '10px', color: LEADS_TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 };

function HitText({ hit, showAddress }) {
  const counts = describeLeadRecordCounts(hit.counts);
  const lists = describeLeadRecordLists(hit.lists);
  const allLists = (hit.lists || []).map((l) => l.name).join(', ');
  const meta = [showAddress ? hit.address : '', lists, counts].filter(Boolean).join(' · ');
  const title = lists && allLists ? `${[showAddress ? hit.address : '', `En ${allLists}`, counts].filter(Boolean).join(' · ')}` : undefined;
  return (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={titleSx}>{hit.label || getLeadRecordKindLabel(hit.itemKind)}</Typography>
      {meta ? <Typography sx={metaSx} title={title}>{meta}</Typography> : null}
    </Box>
  );
}

const hitKey = (hit) => `${hit?.itemKind}:${hit?.id}`;

/**
 * Sin actividad (has_activity:false) = encabezado no seleccionable (salvo `pickAny`).
 * Con `pickAny`, un lote matched:false (viene solo por sus UFs) es solo encabezado: se eligen sus UFs.
 */
const isHitSelectable = (hit, { pickAny = false, isGroup = false, itemKinds = null } = {}) => {
  // `itemKinds` (p. ej. crear campaña: sin lote de catastro): otro tipo queda solo como encabezado.
  if (itemKinds && !itemKinds.includes(hit.itemKind)) return false;
  return pickAny ? !(isGroup && hit.matched === false) : Boolean(hit.hasActivity);
};
/** Tipo no elegible y sin unidades elegibles adentro: no aporta nada, no se lista. */
const isGroupShown = (group, itemKinds) => !itemKinds || itemKinds.includes(group.itemKind)
  || (group.units || []).some((u) => itemKinds.includes(u.itemKind));

/** Lote sin actividad propia (viene por sus UFs) → sus UFs se ven abiertas. */
const isGroupOpenByDefault = (group, pickAny, itemKinds = null) => !group.hasActivity || (pickAny && group.matched === false)
  || Boolean(itemKinds && !itemKinds.includes(group.itemKind));

function ResultRow({
  hit, onPick, showAddress, pickAny = false, pickPrefix = 'Filtrar por', isGroup = false, nav = null, itemKinds = null,
}) {
  const content = (
    <>
      <Typography component="span" sx={kindSx}>{getLeadRecordKindLabel(hit.itemKind)}</Typography>
      <HitText hit={hit} showAddress={showAddress} />
    </>
  );
  const selectable = isHitSelectable(hit, { pickAny, isGroup, itemKinds });
  if (!selectable) return <Box sx={{ ...rowSx, '&:hover': undefined }}>{content}</Box>;
  const name = `${pickPrefix} ${describeLeadRecordFilter(hit)}`;
  if (nav) {
    // Flotante (combobox): opción del listbox; el foco queda en el input.
    const key = hitKey(hit);
    const active = nav.activeKey === key;
    const picked = nav.pickedKeys.includes(key);
    return (
      <Box
        id={nav.optionId(key)}
        role="option"
        aria-selected={active}
        aria-label={picked ? `${name} (elegido)` : name}
        data-active={active}
        onMouseEnter={() => nav.setActiveKey(key)}
        onClick={() => onPick(hit)}
        sx={{ ...rowSx, cursor: 'pointer', '&[data-active="true"]': { bgcolor: 'rgba(255,255,255,0.06)' } }}
      >
        {content}
        {picked ? <CheckIcon sx={{ fontSize: 16, color: LEADS_SELECTED_ACCENT, flexShrink: 0 }} aria-hidden /> : null}
      </Box>
    );
  }
  return (
    <Button onClick={() => onPick(hit)} sx={rowSx} aria-label={name}>
      {content}
    </Button>
  );
}

function ResultGroup({
  group, onPick, pickAny = false, pickPrefix, open, onToggle, nav = null, itemKinds = null,
}) {
  const units = group.units || [];
  return (
    <Box data-testid="lead-record-group" role={nav ? 'group' : undefined} aria-label={nav ? group.label : undefined}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <ResultRow hit={group} onPick={onPick} showAddress isGroup pickAny={pickAny} pickPrefix={pickPrefix} nav={nav} itemKinds={itemKinds} />
        {units.length ? (
          <BlockCollapseToggle
            expanded={open}
            onToggle={onToggle}
            labelExpanded={`Ocultar unidades de ${group.label}`}
            labelCollapsed={`Ver unidades de ${group.label}`}
          />
        ) : null}
      </Box>
      {units.length ? (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ pl: 2, borderLeft: '1px solid rgba(255,255,255,0.08)', ml: 1.5 }}>
            {units.map((unit) => (
              <ResultRow key={unit.id} hit={unit} onPick={onPick} showAddress={false} pickAny={pickAny} pickPrefix={pickPrefix} nav={nav} itemKinds={itemKinds} />
            ))}
          </Box>
        </Collapse>
      ) : null}
    </Box>
  );
}

export const leadRecordDropdownSx = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  zIndex: 20,
  p: 0.75,
  bgcolor: LEADS_PANEL_BG,
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '8px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
};

/** Desplegable flotante sobre un diálogo (`Popper` en portal): z-index y offset / flip. */
export const floatingPopperSx = { zIndex: (theme) => theme.zIndex.modal + 1 };
export const FLOATING_MODIFIERS = [
  { name: 'offset', options: { offset: [0, 4] } },
  { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
];

/**
 * Resultados del buscador de registros (modo “Registros” del buscador
 * principal de Leads). Se muestra como desplegable bajo el input: grupos por
 * lote con UFs desplegables y “Ver más”. Elegir → `onPick({ itemKind, id, label })`.
 * Solo es seleccionable lo que trae `has_activity` (consultas/leads).
 *
 * Carga manual de consultas (`LeadManualInquiryDialog`): `hasActivity={false}`
 * busca todos los registros propios y `pickAny` deja elegir cualquiera
 * (`pickPrefix` = «Elegir»).
 *
 * Flotante (`floating`, buscador de registros de los diálogos vía
 * `LeadRecordPicker`): `Popper` sobre el diálogo (ancho `width`, abajo o
 * arriba si no entra), listbox con opciones; el input conserva el foco y
 * delega ↑ / ↓ / Enter / → / ← en `ref.handleKeyDown(event)` (devuelve
 * `true` si lo usó). La opción activa sale por `onActiveOptionChange(id|null)`
 * para `aria-activedescendant`. `pickedKeys` marca los ya elegidos (✓).
 * El buscador principal del panel no lo usa (desplegable de siempre).
 *
 * @param {{ text: string, ownerUserId?: string|null, onPick: (next: object) => void,
 *   hasActivity?: boolean, pickAny?: boolean, pickPrefix?: string, emptyLabel?: string,
 *   floating?: boolean, open?: boolean, anchorEl?: (HTMLElement|(() => HTMLElement|null)),
 *   width?: number, listId?: string, pickedKeys?: string[],
 *   onActiveOptionChange?: (id: string|null) => void, itemKinds?: string[]|null }} props
 *   `itemKinds`: solo esos tipos se piden (`item_kind`) y se pueden elegir; otro tipo que venga igual
 *   (p. ej. el lote de catastro de una UF) queda como encabezado con sus unidades abiertas.
 */
const LeadRecordSearchResults = forwardRef(function LeadRecordSearchResults({
  text,
  ownerUserId = null,
  onPick,
  hasActivity = undefined,
  pickAny = false,
  pickPrefix = 'Filtrar por',
  emptyLabel = 'Sin resultados con consultas.',
  floating = false,
  open = true,
  anchorEl = null,
  width = undefined,
  listId = 'lead-record-results',
  pickedKeys = [],
  onActiveOptionChange,
  itemKinds = null,
}, ref) {
  const search = useLeadRecordSearch(text, { ownerUserId, hasActivity, itemKinds });
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();
  // Lotes abiertos / cerrados a mano (el default depende del lote).
  const [openOverrides, setOpenOverrides] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const typed = String(text || '').trim();
  const pick = (hit) => {
    if (itemKinds && !itemKinds.includes(hit?.itemKind)) return;
    const next = normalizeLeadRecordFilter(hit);
    if (next) onPick(next);
  };
  const isGroupOpen = (group) => openOverrides[hitKey(group)] ?? isGroupOpenByDefault(group, pickAny, itemKinds);
  const setGroupOpen = (group, value) => setOpenOverrides((prev) => ({ ...prev, [hitKey(group)]: value }));

  const shownGroups = search.groups.filter((group) => isGroupShown(group, itemKinds));
  // Opciones visibles en orden (para ↑ / ↓): lote elegible y, si está abierto, sus UFs.
  const visible = [];
  if (search.status === 'ready' && typed) {
    shownGroups.forEach((group) => {
      if (isHitSelectable(group, { pickAny, isGroup: true, itemKinds })) visible.push({ hit: group, group });
      if (isGroupOpen(group)) {
        (group.units || []).forEach((unit) => {
          if (isHitSelectable(unit, { pickAny, itemKinds })) visible.push({ hit: unit, group });
        });
      }
    });
  }
  const activeIndex = visible.findIndex((v) => hitKey(v.hit) === activeKey);
  const optionId = (key) => `${listId}-opt-${key}`;
  const shown = floating && open;
  const activeOptionId = shown && activeIndex >= 0 ? optionId(activeKey) : null;

  useEffect(() => {
    if (floating) onActiveOptionChange?.(activeOptionId);
  }, [floating, activeOptionId, onActiveOptionChange]);

  const moveTo = (index) => {
    const key = hitKey(visible[index].hit);
    setActiveKey(key);
    document.getElementById(optionId(key))?.scrollIntoView?.({ block: 'nearest' });
  };

  useImperativeHandle(ref, () => ({
    handleKeyDown(event) {
      if (!shown || !visible.length) return false;
      const current = activeIndex >= 0 ? visible[activeIndex] : null;
      switch (event.key) {
        case 'ArrowDown':
          moveTo(activeIndex < 0 ? 0 : (activeIndex + 1) % visible.length);
          break;
        case 'ArrowUp':
          moveTo(activeIndex < 0 ? visible.length - 1 : (activeIndex - 1 + visible.length) % visible.length);
          break;
        case 'Enter':
          if (!current) return false;
          pick(current.hit);
          break;
        case 'ArrowRight':
          if (!current || current.hit !== current.group || !(current.group.units || []).length || isGroupOpen(current.group)) return false;
          setGroupOpen(current.group, true);
          break;
        case 'ArrowLeft':
          if (!current || !isGroupOpen(current.group) || !(current.group.units || []).length) return false;
          setGroupOpen(current.group, false);
          setActiveKey(isHitSelectable(current.group, { pickAny, isGroup: true, itemKinds }) ? hitKey(current.group) : null);
          break;
        default:
          return false;
      }
      event.preventDefault();
      return true;
    },
  }));

  const nav = floating ? { activeKey, setActiveKey, optionId, pickedKeys } : null;

  let body = null;
  if (typed && typed.length < LEAD_RECORD_SEARCH_MIN) {
    body = <Typography sx={{ ...metaSx, px: 1 }}>Escribí al menos {LEAD_RECORD_SEARCH_MIN} caracteres.</Typography>;
  } else if (search.status === 'loading' && !search.groups.length) {
    body = <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={18} /></Box>;
  } else if (search.status === 'unavailable' || search.status === 'error') {
    body = <Typography sx={{ ...metaSx, px: 1 }} role="alert">{search.error}</Typography>;
  } else if (search.status === 'ready' && typed) {
    body = shownGroups.length ? (
      <Box
        ref={scrollRef}
        id={floating ? listId : undefined}
        role={floating ? 'listbox' : undefined}
        aria-label={floating ? 'Resultados de lote o unidad' : undefined}
        sx={{
          ...DOCK_SCROLLBAR_SX,
          maxHeight: 'min(320px, 50vh)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          ...getScrollEdgeFadeSx(fadeTop, fadeBottom),
        }}
        data-testid="lead-record-results"
      >
        {shownGroups.map((group) => (
          <ResultGroup
            key={hitKey(group)}
            group={group}
            onPick={pick}
            itemKinds={itemKinds}
            pickAny={pickAny}
            pickPrefix={pickPrefix}
            open={isGroupOpen(group)}
            onToggle={() => setGroupOpen(group, !isGroupOpen(group))}
            nav={nav}
          />
        ))}
        {search.truncated ? (
          <Typography sx={{ ...metaSx, px: 1, whiteSpace: 'normal' }}>Hay muchos resultados: sumá más palabras.</Typography>
        ) : null}
        {search.hasMore ? (
          <Button
            size="small"
            onClick={search.loadMore}
            disabled={search.loadingMore}
            sx={{ ...leadsSummaryClearButtonSx, alignSelf: 'center', color: LEADS_TEXT_SECONDARY }}
          >
            {search.loadingMore ? 'Cargando…' : 'Ver más'}
          </Button>
        ) : null}
      </Box>
    ) : (
      <Typography sx={{ ...metaSx, px: 1 }}>{emptyLabel}</Typography>
    );
  }
  if (!body) return null;
  if (floating) {
    return (
      <Popper
        open={shown}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={FLOATING_MODIFIERS}
        sx={floatingPopperSx}
        style={{ width }}
      >
        <Box
          sx={{ ...leadRecordDropdownSx, position: 'static' }}
          role="region"
          aria-label="Resultados de lote o unidad"
          // Clic / scroll en la lista no saca el foco del input (no se cierra).
          onMouseDown={(e) => e.preventDefault()}
        >
          {body}
        </Box>
      </Popper>
    );
  }
  return (
    <Box
      sx={leadRecordDropdownSx}
      role="region"
      aria-label="Resultados de lote o unidad"
    >
      {body}
    </Box>
  );
});

export default LeadRecordSearchResults;
